import { Reducer } from 'redux'
import { UpdateQuestionPayload } from './actions'
import { Section } from '../sectionsList/reducer'

export interface Links {
  nextSection?: string
  previousSection?: string
}

export interface Answers {
  answer: string | null
  answerId?: string
  assessmentId?: string
  notes?: string
  assessmentProgressBarPercentage?: number
  isSubSectionPassed?: boolean | null
  questionId?: string
  questionIdRef?: string
  sectionStatus?: string
  subSectionId?: string
  isSectionPassed?: boolean | null
}

export interface AnswersState {
  answerId: string
  questionId: string
  sectionStatus: string
  assessmentProgressBarPercentage: number
  answer?: string
}

export enum AnswersActionTypes {
  SET_ANSWERS = 'SET_ANSWERS',
  SET_ERROR = 'SET_ERROR',
  SET_ANSWER_ID = 'SET_ANSWER_ID',
  CLEAR_ANSWERS = 'CLEAR_ANSWERS',
}

type AnswerActionType =
  | {
      type: AnswersActionTypes.SET_ANSWERS
      payload: {
        answerId: string
        questionId: string
        sectionStatus: string
        assessmentProgressBarPercentage: number
      }
    }
  | {
      type: AnswersActionTypes.SET_ANSWER_ID
      payload: string
    }
  | {
      type: AnswersActionTypes.CLEAR_ANSWERS
    }
  | {
      type: AnswersActionTypes.SET_ERROR
      payload: string
    }
export const initialStateAnswer: AnswersState = {
  answerId: '',
  questionId: '',
  sectionStatus: '',
  assessmentProgressBarPercentage: 0,
}
export const answerReducer: Reducer<AnswersState, AnswerActionType> = (
  state = initialStateAnswer,
  action
) => {
  switch (action.type) {
    case AnswersActionTypes.SET_ERROR: {
      return {
        ...state,
        error: action.payload,
      }
    }
    case AnswersActionTypes.SET_ANSWERS: {
      return {
        ...state,
        answerId: action.payload?.answerId || '',
        questionId: action.payload?.questionId,
        sectionStatus: action.payload?.sectionStatus,
        assessmentProgressBarPercentage:
          action.payload?.assessmentProgressBarPercentage,
      }
    }
    case AnswersActionTypes.SET_ANSWER_ID: {
      return {
        ...state,
        answerId: action.payload || '',
      }
    }
    case AnswersActionTypes.CLEAR_ANSWERS: {
      return {
        ...state,
        answerId: '',
        questionId: '',
        sectionStatus: '',
        assessmentProgressBarPercentage: 0,
      }
    }
    default: {
      return state
    }
  }
}
export interface Links {
  next_section?: string
  prev_section?: string
}

// export interface Answer {
//   answer: string;
//   answerId?: string;
//   assessmentId?: string;
//   notes: string;
// }
export interface Answer {
  answer: string | null
  answerId?: string
  assessmentId?: string
  notes?: string
  questionId?: string
}

export interface Question {
  questionId: string
  questionIdRef: string
  question: string
  positiveText: string
  negativeText: string
  possibleAnswerValues: string[]
  metaData: any // refine if structure known
  answerId?: string
  answer?: string | null
}

export interface QuestionSummary {
  subSectionId: string
  subSectionTitle: string
  subSectionDescription: string
  questions: Question[]
  selectedAnswers: Answer[]
  notes: string
  isSubSectionPassed: boolean | null | undefined
}

export interface QuestionSection {
  sectionId: string
  sectionName: string
  assessmentId: string
  questionSummaries: QuestionSummary[]
  totalQuestionsCount: number
  links: Links
  isSectionPassed: boolean | null
}

export interface SectionWithQuestion extends QuestionSection, Section {}

export interface QuestionsState {
  section: QuestionSection | null
  selectedQuestion?: Question
  allSectionQuestion?: SectionWithQuestion[] | null
  error?: string

  questions?: Question[]
  totalQuestionsCount?: number
}

export const initialState: QuestionsState = {
  section: null,
  selectedQuestion: undefined,
  allSectionQuestion: undefined,
  error: undefined,
}

export enum QuestionsActionTypes {
  SET_SECTION = 'SET_SECTION',
  SET_ALL_SECTION = 'SET_ALL_SECTION',
  SET_SELECTED_QUESTION = 'SET_SELECTED_QUESTION',
  UPDATE_QUESTION = 'UPDATE_QUESTION',
  CLEAR_SECTION = 'CLEAR_SECTION',
  SET_ERROR = 'SET_ERROR',
  CLEAR_QUESTION = 'CLEAR_QUESTION',
}

type ActionType =
  | {
      type: QuestionsActionTypes.SET_SECTION
      payload: QuestionSection
    }
  | {
      type: QuestionsActionTypes.SET_ALL_SECTION
      payload: SectionWithQuestion[]
    }
  | {
      type: QuestionsActionTypes.SET_SELECTED_QUESTION
      payload: Question
    }
  | {
      type: QuestionsActionTypes.UPDATE_QUESTION
      payload: UpdateQuestionPayload
    }
  | {
      type: QuestionsActionTypes.CLEAR_SECTION
    }
  | {
      type: QuestionsActionTypes.CLEAR_QUESTION
    }
  | {
      type: QuestionsActionTypes.SET_ERROR
      payload: string
    }

export const reducer: Reducer<QuestionsState, ActionType> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case QuestionsActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      }

    case QuestionsActionTypes.SET_SECTION:
      return {
        ...state,
        section: action.payload,
      }
    case QuestionsActionTypes.SET_ALL_SECTION:
      return {
        ...state,
        allSectionQuestion: action.payload as SectionWithQuestion[],
      }

    case QuestionsActionTypes.SET_SELECTED_QUESTION:
      return {
        ...state,
        selectedQuestion: action.payload,
      }

    case QuestionsActionTypes.CLEAR_SECTION:
      return {
        ...state,
        section: null,
        selectedQuestion: undefined,
      }
    case QuestionsActionTypes.CLEAR_QUESTION: {
      return {
        ...state,
        questions: [],
        selectedQuestion: {} as Question,
        totalQuestionsCount: 0,
      }
    }

    case QuestionsActionTypes.UPDATE_QUESTION: {
      if (!state.section) return state

      return {
        ...state,
        section: {
          ...state.section,
          isSectionPassed: action.payload.isSectionPassed,
          questionSummaries: state.section.questionSummaries.map(summary =>
            summary.subSectionId === action.payload.subSectionId
              ? {
                  ...summary,
                  isSubSectionPassed:
                    action.payload.updatedQuestion.isSubSectionPassed,
                  notes: action.payload.updatedQuestion.notes,
                  questions: summary.questions.map(q => {
                    const updatedQ =
                      action.payload.updatedQuestion.selectedAnswers.find(
                        uq => uq.questionId === q.questionId
                      )
                    return updatedQ ? { ...q, ...updatedQ } : q
                  }),
                  selectedAnswers:
                    action.payload.updatedQuestion.selectedAnswers,
                }
              : summary
          ),
        },
      }
    }

    default:
      return state
  }
}
